import type { BadgeTone } from './Badge';
import {
  followUpStatusLabel,
  invoiceStatusLabel,
  jobStatusLabel,
  keysOf,
  leadDispositionLabel,
  packageStatusLabel,
  phaseOf,
  pipelinePhaseLabel,
  pipelineStateLabel,
  proposalStatusLabel,
  type FollowUpStatus,
  type InvoiceStatus,
  type JobStatus,
  type LeadDisposition,
  type OpportunityStatus,
  type PackageStatus,
  type PipelinePhase,
  type ProposalStatus,
} from './vocabulary';

/** The vocabularies a status chip can draw. */
export type StatusChipKind =
  'job' | 'pipeline' | 'lead' | 'proposal' | 'invoice' | 'package' | 'follow-up';

/** The shape drawn beside a status label, the channel that survives without colour. */
export type StatusMarker =
  'ring' | 'dot' | 'chevron' | 'square' | 'check' | 'pause' | 'slash' | 'funnel';

export interface StatusLook {
  readonly marker: StatusMarker;
  readonly tone: BadgeTone;
  /** Only jobs: the `--status-*` palette the dispatch board shares, which outranks the tone. */
  readonly token?: string;
}

export interface StatusView {
  readonly label: string;
  readonly look: StatusLook;
  /** Only pipeline states: the stage a person files the state under. */
  readonly phase?: string;
}

// A code the server adds before the interface learns it still draws, in neutral.
const FALLBACK_LOOK: StatusLook = { marker: 'ring', tone: 'neutral' };

const JOB_LOOKS: Record<JobStatus, StatusLook> = {
  SCHEDULED: { marker: 'ring', tone: 'accent', token: 'scheduled' },
  DISPATCHED: { marker: 'dot', tone: 'info', token: 'dispatched' },
  EN_ROUTE: { marker: 'chevron', tone: 'warning', token: 'en-route' },
  ON_SITE: { marker: 'square', tone: 'success', token: 'on-site' },
  COMPLETED: { marker: 'check', tone: 'neutral', token: 'completed' },
  ON_HOLD: { marker: 'pause', tone: 'neutral', token: 'on-hold' },
  CANCELLED: { marker: 'slash', tone: 'neutral', token: 'cancelled' },
};

// The marker follows the phase so a column of mixed states still groups at a glance; the tone
// follows the state, because "Lost" and "Closed" share a phase but not a meaning.
const PHASE_MARKERS: Record<PipelinePhase, StatusMarker> = {
  LEAD: 'ring',
  CONSULT: 'dot',
  PROPOSE: 'funnel',
  DECIDE: 'chevron',
  INSTALL: 'square',
  AFTERCARE: 'check',
  CLOSED: 'slash',
};

const PIPELINE_TONES: Record<OpportunityStatus, BadgeTone> = {
  NEW: 'neutral',
  CONTACTED: 'accent',
  QUALIFIED: 'accent',
  APPOINTMENT_SCHEDULED: 'info',
  CONSULTATION_COMPLETE: 'info',
  PROPOSAL_PENDING: 'warning',
  HUMAN_FOLLOW_UP: 'warning',
  SOLD: 'success',
  INSTALLATION_SCHEDULED: 'success',
  INSTALLED: 'success',
  POST_INSTALL_FOLLOW_UP: 'info',
  LOST: 'danger',
  NO_DECISION: 'warning',
  CLOSED: 'neutral',
};

const LEAD_LOOKS: Record<LeadDisposition, StatusLook> = {
  OPEN: { marker: 'ring', tone: 'neutral' },
  CONTACTED: { marker: 'dot', tone: 'accent' },
  QUALIFIED: { marker: 'check', tone: 'success' },
  CLOSED: { marker: 'slash', tone: 'neutral' },
};

const PROPOSAL_LOOKS: Record<ProposalStatus, StatusLook> = {
  DRAFT: { marker: 'ring', tone: 'neutral' },
  PENDING_APPROVAL: { marker: 'pause', tone: 'warning' },
  PRESENTED: { marker: 'chevron', tone: 'info' },
  ACCEPTED: { marker: 'check', tone: 'success' },
  DECLINED: { marker: 'slash', tone: 'danger' },
};

// A voided bill is cancelled, not unpaid: neutral, so it never reads like a debt.
const INVOICE_LOOKS: Record<InvoiceStatus, StatusLook> = {
  DRAFT: { marker: 'ring', tone: 'neutral' },
  SENT: { marker: 'chevron', tone: 'info' },
  VIEWED: { marker: 'dot', tone: 'info' },
  PAID: { marker: 'check', tone: 'success' },
  OVERDUE: { marker: 'square', tone: 'danger' },
  VOID: { marker: 'slash', tone: 'neutral' },
};

const PACKAGE_LOOKS: Record<PackageStatus, StatusLook> = {
  DRAFT: { marker: 'ring', tone: 'neutral' },
  READY: { marker: 'dot', tone: 'accent' },
  SCHEDULED: { marker: 'check', tone: 'success' },
  CANCELLED: { marker: 'slash', tone: 'neutral' },
};

const FOLLOW_UP_LOOKS: Record<FollowUpStatus, StatusLook> = {
  OPEN: { marker: 'ring', tone: 'accent' },
  DONE: { marker: 'check', tone: 'success' },
  ESCALATED: { marker: 'chevron', tone: 'danger' },
  CANCELLED: { marker: 'slash', tone: 'neutral' },
};

function lookUp(looks: Readonly<Record<string, StatusLook>>, value: string): StatusLook {
  return (Object.hasOwn(looks, value) ? looks[value] : undefined) ?? FALLBACK_LOOK;
}

function pipelineView(value: string): StatusView {
  const label = pipelineStateLabel(value);
  const phase = phaseOf(value);
  if (phase === undefined) {
    return { label, look: FALLBACK_LOOK };
  }
  const tone = PIPELINE_TONES[value as OpportunityStatus];
  return { label, look: { marker: PHASE_MARKERS[phase], tone }, phase: pipelinePhaseLabel(phase) };
}

const DESCRIBERS: Record<StatusChipKind, (value: string) => StatusView> = {
  job: (value) => ({ label: jobStatusLabel(value), look: lookUp(JOB_LOOKS, value) }),
  pipeline: pipelineView,
  lead: (value) => ({ label: leadDispositionLabel(value), look: lookUp(LEAD_LOOKS, value) }),
  proposal: (value) => ({ label: proposalStatusLabel(value), look: lookUp(PROPOSAL_LOOKS, value) }),
  invoice: (value) => ({ label: invoiceStatusLabel(value), look: lookUp(INVOICE_LOOKS, value) }),
  package: (value) => ({ label: packageStatusLabel(value), look: lookUp(PACKAGE_LOOKS, value) }),
  'follow-up': (value) => ({
    label: followUpStatusLabel(value),
    look: lookUp(FOLLOW_UP_LOOKS, value),
  }),
};

/** Every code each family draws, in the order its vocabulary declares them. */
export const STATUS_CHIP_VALUES: Record<StatusChipKind, readonly string[]> = {
  job: keysOf(JOB_LOOKS),
  pipeline: keysOf(PIPELINE_TONES),
  lead: keysOf(LEAD_LOOKS),
  proposal: keysOf(PROPOSAL_LOOKS),
  invoice: keysOf(INVOICE_LOOKS),
  package: keysOf(PACKAGE_LOOKS),
  'follow-up': keysOf(FOLLOW_UP_LOOKS),
};

/**
 * What a status chip shows for a code: its wording, its marker and tone, and for a pipeline state
 * the phase it sits under.
 *
 * @param kind the vocabulary the code belongs to
 * @param value the code the server sent; an unknown one is worded in plain words and drawn neutral
 * @returns everything the chip needs to draw
 */
export function describeStatus(kind: StatusChipKind, value: string): StatusView {
  return DESCRIBERS[kind](value);
}
